import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, PieChart, Store, LineChart, Layers, Wallet as WalletIcon,
  Download, Upload, ArrowLeftRight, Coins, Lock, Zap, FileText, ShieldCheck,
  Bell, LifeBuoy, User, Shield, Settings, LogOut, Menu, X, Gift, Layers3, Repeat,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DashboardPanel, PortfolioPanel, MarketplacePanel, InvestmentsPanel, AssetDetailsPanel,
  WalletPanel, DepositPanel, WithdrawPanel, TransactionsPanel, DividendsPanel, StakingPanel,
  AutoInvestPanel, ReportsPanel, KycPanel, NotificationsPanel, SupportPanel, ProfilePanel,
  SecurityPanel, SettingsPanel, OfferingModal, SubscriptionsPanel, ReferralsPanel,
  SecondaryMarketPanel, fmt,
} from "@/components/dashboard/Panels";

const NAV = [
  { k: "dashboard", label: "Dashboard", Icon: LayoutDashboard, group: "OVERVIEW" },
  { k: "portfolio", label: "Portfolio", Icon: PieChart, group: "OVERVIEW" },
  { k: "marketplace", label: "Marketplace", Icon: Store, group: "INVEST" },
  { k: "investments", label: "Investments", Icon: LineChart, group: "INVEST" },
  { k: "subscriptions", label: "Subscriptions", Icon: Layers3, group: "INVEST" },
  { k: "secondary", label: "Secondary Market", Icon: Repeat, group: "INVEST" },
  { k: "asset-details", label: "Asset Details", Icon: Layers, group: "INVEST" },
  { k: "wallet", label: "Wallet", Icon: WalletIcon, group: "FUNDS" },
  { k: "deposit", label: "Deposit", Icon: Download, group: "FUNDS" },
  { k: "withdraw", label: "Withdraw", Icon: Upload, group: "FUNDS" },
  { k: "transactions", label: "Transactions", Icon: ArrowLeftRight, group: "FUNDS" },
  { k: "dividends", label: "Dividends", Icon: Coins, group: "EARN" },
  { k: "staking", label: "Staking", Icon: Lock, group: "EARN" },
  { k: "auto-invest", label: "Auto Invest", Icon: Zap, group: "EARN" },
  { k: "referrals", label: "Referrals", Icon: Gift, group: "EARN" },
  { k: "reports", label: "Reports", Icon: FileText, group: "ACCOUNT" },
  { k: "kyc", label: "KYC", Icon: ShieldCheck, group: "ACCOUNT" },
  { k: "notifications", label: "Notifications", Icon: Bell, group: "ACCOUNT" },
  { k: "support", label: "Support", Icon: LifeBuoy, group: "ACCOUNT" },
  { k: "profile", label: "Profile", Icon: User, group: "SETTINGS" },
  { k: "security", label: "Security", Icon: Shield, group: "SETTINGS" },
  { k: "settings", label: "Settings", Icon: Settings, group: "SETTINGS" },
];

const GROUPS = ["OVERVIEW", "INVEST", "FUNDS", "EARN", "ACCOUNT", "SETTINGS"];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [view, setView] = useState(() => new URLSearchParams(window.location.search).get("v") || "dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [markets, setMarkets] = useState([]);
  const [portfolio, setPortfolio] = useState({ cash: 0, holdings: [], transactions: [] });
  const [wallet, setWallet] = useState({ cash: 0, addresses: {}, deposits: [], withdrawals: [] });
  const [kyc, setKyc] = useState({ status: "NOT_SUBMITTED" });
  const [notifUnread, setNotifUnread] = useState(0);
  const [invModal, setInvModal] = useState(null);
  const [invAmount, setInvAmount] = useState("");
  const [invBusy, setInvBusy] = useState(false);
  const [offering, setOffering] = useState(null);
  const [activeAsset, setActiveAsset] = useState(null);

  useEffect(() => {
    if (user === false) navigate("/login");
  }, [user, navigate]);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    p.set("v", view);
    window.history.replaceState({}, "", `${window.location.pathname}?${p.toString()}`);
  }, [view]);

  const loadMarkets = useCallback(async () => {
    try { const { data } = await api.get("/crypto/markets"); setMarkets(data); } catch { /* keep prior */ }
  }, []);
  const loadPortfolio = useCallback(async () => {
    try { const { data } = await api.get("/portfolio"); setPortfolio(data); } catch { /* noop */ }
  }, []);
  const loadWallet = useCallback(async () => {
    try { const { data } = await api.get("/wallet"); setWallet(data); } catch { /* noop */ }
  }, []);
  const loadKyc = useCallback(async () => {
    try { const { data } = await api.get("/kyc"); setKyc(data); } catch { /* noop */ }
  }, []);
  const loadNotifCount = useCallback(async () => {
    try { const { data } = await api.get("/notifications"); setNotifUnread(data.unread); } catch { /* noop */ }
  }, []);

  const refreshAll = useCallback(() => {
    loadPortfolio(); loadWallet(); loadKyc(); loadNotifCount();
  }, [loadPortfolio, loadWallet, loadKyc, loadNotifCount]);

  useEffect(() => {
    if (!user) return;
    loadMarkets(); refreshAll();
    const id = setInterval(loadMarkets, 30000);
    return () => clearInterval(id);
  }, [user, loadMarkets, refreshAll]);

  const priceMap = useMemo(() => Object.fromEntries(markets.map((m) => [m.id, m])), [markets]);
  const ctx = { user, markets, portfolio, priceMap, wallet, kyc };

  const openInvest = (m) => { setInvModal(m); setInvAmount(""); };
  const openAsset = (o) => setOffering(o);

  const submitInvest = async () => {
    const usd = parseFloat(invAmount);
    if (!usd || usd <= 0) { toast.error("Enter a valid amount"); return; }
    if (usd > portfolio.cash) { toast.error("Insufficient balance"); return; }
    setInvBusy(true);
    try {
      await api.post("/invest", { coin_id: invModal.id, symbol: invModal.symbol, name: invModal.name, usd_amount: usd, price: invModal.price });
      toast.success(`Invested $${fmt(usd)} in ${invModal.symbol}`);
      setInvModal(null); setInvAmount("");
      loadPortfolio();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setInvBusy(false);
  };

  if (!user) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <img src="/brand/auvra-logo-trim.png" alt="AUVRA" className="h-10 w-auto animate-pulse" />
    </div>
  );

  const currentNav = NAV.find((n) => n.k === view);

  return (
    <div className="min-h-screen bg-black text-white flex" data-testid="dashboard-shell">
      {/* Sidebar */}
      <aside className={`fixed lg:sticky top-0 h-screen w-64 bg-black border-r border-white/10 z-40 transition-transform ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"} flex flex-col`} data-testid="sidebar">
        <div className="p-5 border-b border-white/10 flex items-center justify-between">
          <a href="/" className="flex items-center"><img src="/brand/auvra-logo-trim.png" alt="AUVRA" className="h-7 w-auto" /></a>
          <button className="lg:hidden text-secondary-auvra" onClick={() => setSidebarOpen(false)}><X className="w-5 h-5" /></button>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-6">
          {GROUPS.map((g) => (
            <div key={g}>
              <div className="text-[10px] tracking-[0.2em] text-secondary-auvra/60 px-3 mb-2">{g}</div>
              <div className="space-y-1">
                {NAV.filter((n) => n.group === g).map((n) => {
                  const active = view === n.k;
                  return (
                    <button key={n.k} onClick={() => { setView(n.k); setSidebarOpen(false); }} data-testid={`nav-${n.k}`}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${active ? "bg-auvra-blue/15 text-white border border-auvra-blue/30" : "text-secondary-auvra hover:text-white hover:bg-white/[0.03] border border-transparent"}`}>
                      <n.Icon className="w-4 h-4 flex-shrink-0" />
                      <span className="flex-1 text-left">{n.label}</span>
                      {n.k === "notifications" && notifUnread > 0 && (
                        <span className="text-[10px] bg-auvra-blue text-white rounded-full px-1.5 py-0.5" data-testid="notif-badge">{notifUnread}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <button onClick={logout} data-testid="logout-btn" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-secondary-auvra hover:text-white hover:bg-white/[0.03] transition-colors">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      {sidebarOpen && <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0">
        <header className="border-b border-white/10 sticky top-0 z-20 bg-black/80 backdrop-blur-xl">
          <div className="px-4 md:px-8 flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <button className="lg:hidden" onClick={() => setSidebarOpen(true)} data-testid="open-sidebar"><Menu className="w-5 h-5" /></button>
              <div className="text-sm text-secondary-auvra hidden sm:block">Dashboard · <span className="text-white">{currentNav?.label}</span></div>
            </div>
            <div className="flex items-center gap-4">
              <button onClick={() => setView("notifications")} className="relative text-secondary-auvra hover:text-white transition-colors" data-testid="header-notif">
                <Bell className="w-5 h-5" />
                {notifUnread > 0 && <span className="absolute -top-1 -right-1 text-[9px] bg-auvra-blue text-white rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">{notifUnread}</span>}
              </button>
              <div className="flex items-center gap-2">
                {user.picture ? <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" /> :
                  <div className="w-8 h-8 rounded-full bg-auvra-blue/20 flex items-center justify-center text-xs">{user.name?.[0]}</div>}
                <span className="text-sm text-secondary-auvra hidden sm:block" data-testid="dashboard-user">{user.name}</span>
              </div>
            </div>
          </div>
        </header>

        <main className="p-4 md:p-8" data-testid="dashboard-main">
          {view === "dashboard" && <DashboardPanel ctx={ctx} />}
          {view === "portfolio" && <PortfolioPanel ctx={ctx} />}
          {view === "marketplace" && <MarketplacePanel ctx={ctx} onInvest={openInvest} onOpenAsset={openAsset} />}
          {view === "investments" && <InvestmentsPanel ctx={ctx} />}
          {view === "subscriptions" && <SubscriptionsPanel />}
          {view === "secondary" && <SecondaryMarketPanel />}
          {view === "asset-details" && <AssetDetailsPanel ctx={ctx} activeAsset={activeAsset} setActiveAsset={setActiveAsset} />}
          {view === "wallet" && <WalletPanel ctx={ctx} setView={setView} />}
          {view === "deposit" && <DepositPanel ctx={ctx} refresh={loadWallet} />}
          {view === "withdraw" && <WithdrawPanel ctx={ctx} refresh={() => { loadWallet(); refreshAll(); }} />}
          {view === "transactions" && <TransactionsPanel />}
          {view === "dividends" && <DividendsPanel />}
          {view === "staking" && <StakingPanel />}
          {view === "auto-invest" && <AutoInvestPanel ctx={ctx} />}
          {view === "referrals" && <ReferralsPanel />}
          {view === "reports" && <ReportsPanel />}
          {view === "kyc" && <KycPanel refresh={loadKyc} />}
          {view === "notifications" && <NotificationsPanel refresh={loadNotifCount} />}
          {view === "support" && <SupportPanel />}
          {view === "profile" && <ProfilePanel ctx={ctx} refresh={() => window.location.reload()} />}
          {view === "security" && <SecurityPanel ctx={ctx} />}
          {view === "settings" && <SettingsPanel />}
        </main>
      </div>

      {/* Crypto Invest modal */}
      <Dialog open={!!invModal} onOpenChange={(o) => !o && setInvModal(null)}>
        <DialogContent className="bg-auvra-elevated border border-white/10 text-white sm:max-w-sm" data-testid="invest-modal">
          <DialogHeader><DialogTitle className="flex items-center gap-3">
            {invModal && <img src={invModal.image} alt="" className="w-7 h-7 rounded-full" />}
            Invest in {invModal?.symbol}
          </DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="flex justify-between text-sm"><span className="text-secondary-auvra">Live price</span><span className="font-mono">${invModal && fmt(invModal.price)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-secondary-auvra">Available cash</span><span className="font-mono">${fmt(portfolio.cash)}</span></div>
            <input type="number" value={invAmount} onChange={(e) => setInvAmount(e.target.value)} placeholder="Amount in USD" data-testid="invest-amount"
              className="w-full bg-black border border-white/15 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-auvra-blue" />
            {invAmount > 0 && invModal && (
              <p className="text-xs text-secondary-auvra" data-testid="invest-units">≈ {fmt(invAmount / invModal.price, 6)} {invModal.symbol}</p>
            )}
            <div className="flex gap-2">
              {[500, 1000, 5000].map((v) => (
                <button key={v} onClick={() => setInvAmount(String(v))} className="flex-1 text-xs border border-white/15 hover:border-auvra-blue rounded-lg py-2 transition-colors">${v}</button>
              ))}
            </div>
            <button onClick={submitInvest} disabled={invBusy} data-testid="confirm-invest-btn"
              className="w-full bg-auvra-blue hover:bg-auvra-blue/90 disabled:opacity-60 text-white font-medium py-3.5 rounded-xl transition-all">
              {invBusy ? "Processing…" : "Confirm Investment"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* RWA offering detail */}
      <OfferingModal
        offering={offering}
        aurCredit={user?.aur_credit || 0}
        onClose={() => setOffering(null)}
        onSubscribe={async (o, ticket, aurApply) => {
          try {
            const { data } = await api.post("/subscriptions", {
              offering_id: o.id,
              ticket,
              aur_credit_apply: aurApply || 0,
              eligibility: o.eligibility,
            });
            toast.success(`Subscribed to ${o.title} — $${data.ticket} allocated${aurApply ? `, ${aurApply} AUR applied` : ""}`);
            setOffering(null);
            loadPortfolio(); loadWallet();
            // refresh user aur_credit
            try { const me = await api.get("/auth/me"); if (me.data) { /* auth context is source of truth but api.get('/auth/me') may need explicit refresh in AuthContext */ } } catch { /* noop */ }
            setView("subscriptions");
          } catch (e) {
            toast.error(apiError(e.response?.data?.detail));
          }
        }}
      />
    </div>
  );
}
